import { useEffect, useRef, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, Clock, Users, Calendar, ArrowLeft, Sparkles, Check, X, ChevronDown, Mountain, Flag, Navigation } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { getStatus, showPrice, dateDisplay } from "@/lib/status";
import { MaskedLines } from "@/components/Kinetic";
import { TierCards } from "@/components/TierCards";
import { WaitlistForm } from "@/components/WaitlistForm";
import { ApplicationForm } from "@/components/ApplicationForm";

const DEFAULT_WHO_FOR = [
  "You want to travel but don't want to travel alone.",
  "You want to meet new people.",
  "You're open to spontaneous experiences.",
  "You prefer memories over tourist checklists.",
  "You're between 18 and 45.",
];

const DEFAULT_EXCLUDED = [
  "Flights (unless explicitly listed)",
  "Transportation to the meeting destination",
  "Travel insurance",
  "Personal expenses",
  "Meals not listed",
  "Optional activities",
];

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-40px" },
  transition: { duration: 0.55, ease: "easeOut" },
};

export default function TripPage() {
  const { slug } = useParams();
  const [trip, setTrip] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [openFaq, setOpenFaq] = useState(null);
  const waitlistRef = useRef(null);

  useSEO(
    trip?.seo_title || (trip ? `${trip.title} — Roamies` : "Roamies Trip"),
    trip?.seo_description || trip?.tagline || "Small-group trips for solo travelers ages 18–45."
  );

  useEffect(() => {
    setTrip(null);
    setNotFound(false);
    api.get(`/trips/${slug}`).then((r) => setTrip(r.data)).catch(() => setNotFound(true));
    window.scrollTo(0, 0);
  }, [slug]);

  if (notFound) {
    return (
      <div className="py-40 text-center" data-testid="trip-not-found">
        <h1 className="font-display text-5xl uppercase">Trip not found</h1>
        <Link to="/trips" className="text-flame font-bold uppercase tracking-widest text-sm mt-6 inline-block">← Back to all trips</Link>
      </div>
    );
  }

  if (!trip) {
    return <div className="py-40 px-6 text-ink/50 font-semibold" data-testid="trip-loading">Loading trip...</div>;
  }

  const status = getStatus(trip.status);
  const words = trip.title.split(" ");
  const titleLines = [];
  for (let i = 0; i < words.length; i += 2) titleLines.push(words.slice(i, i + 2).join(" "));
  const dateLabel = dateDisplay(trip);
  const whoFor = trip.who_for?.length ? trip.who_for : DEFAULT_WHO_FOR;
  const excluded = trip.excluded?.length ? trip.excluded : DEFAULT_EXCLUDED;
  const hasPricing = showPrice(trip.status) && (trip.tiers?.basic?.price || trip.price_label);
  const includedTitle = trip.status === "booking_open" ? "What's included" : "Planned inclusions";
  const meetingFields = [
    ["Recommended airport", trip.recommended_airport],
    ["Meeting city", trip.meeting_city],
    ["Meeting address", trip.meeting_address],
    ["Meeting time", trip.meeting_time],
    ["Departure / drop-off", trip.departure_point],
    ["Airport transfers", trip.airport_transfer_info],
  ].filter(([, v]) => v);

  const scrollToWaitlist = () => waitlistRef.current?.scrollIntoView({ behavior: "smooth" });
  const ctaAction = () => {
    if (trip.status === "sold_out") { window.location.href = "/trips"; return; }
    if (trip.status === "completed") { document.getElementById("trip-highlights")?.scrollIntoView({ behavior: "smooth" }); return; }
    scrollToWaitlist();
  };

  const quickInfo = [
    { icon: Calendar, label: "Dates", value: dateLabel },
    { icon: MapPin, label: "Destination", value: trip.destination },
    { icon: Clock, label: "Duration", value: trip.duration },
    { icon: Users, label: "Group size", value: trip.group_size },
    { icon: Flag, label: "Age requirement", value: `Ages ${trip.age_min || 18}–${trip.age_max || 45}` },
    { icon: Users, label: "Who can join", value: trip.solo_policy === "solo_only" ? "Solo travelers only" : "Solo travelers + friends welcome" },
    { icon: Navigation, label: "Meeting point", value: trip.meeting_location || "Shared with the crew" },
    { icon: Sparkles, label: "Trip style", value: trip.trip_style || "Curated group adventure" },
    { icon: Mountain, label: "Activity level", value: trip.activity_level || "Easy to moderate" },
  ].filter((i) => i.value);

  const deadlines = [
    ["Booking opens", trip.booking_open_date],
    ["Booking deadline", trip.booking_deadline],
    ["Deposit deadline", trip.deposit_deadline],
    ["Final payment", trip.final_payment_deadline],
  ].filter(([, v]) => v);

  return (
    <div data-testid="trip-detail-page">
      {/* A. CINEMATIC HERO */}
      <section className="relative min-h-[80vh] flex items-end overflow-hidden bg-ink">
        <div className="absolute inset-0">
          <motion.img src={trip.image} alt={trip.title} className="w-full h-full object-cover"
            initial={{ scale: 1.08, filter: "brightness(0.7)" }}
            animate={{ scale: 1, filter: "brightness(1)" }}
            transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }} />
        </div>
        <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/30 to-black/10" />
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 pb-16 pt-32 w-full">
          <Link to="/trips" data-testid="trip-back-link"
            className="inline-flex items-center gap-2 text-cream/80 text-xs font-bold uppercase tracking-widest hover:text-flame transition-colors mb-6">
            <ArrowLeft className="w-4 h-4" /> All trips
          </Link>
          <div className="flex flex-wrap gap-2 mb-5">
            {trip.drop_number && (
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase bg-cream text-ink px-3 py-1.5" data-testid="trip-drop-number">Roamies {trip.drop_number}</span>
            )}
            <span className={`text-[10px] font-bold tracking-[0.15em] uppercase px-3 py-1.5 ${status.badge}`} data-testid="trip-status-badge">{status.label}</span>
          </div>
          <h1 className="font-display text-5xl sm:text-6xl md:text-7xl uppercase leading-[0.88] text-cream" data-testid="trip-title">
            <MaskedLines delay={0.3} lines={titleLines} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.9 }}
            className="mt-4 text-xs font-bold tracking-[0.2em] uppercase text-cream/70" data-testid="trip-hero-meta">
            {trip.destination} · {dateLabel} · {trip.duration} · Ages {trip.age_min || 18}–{trip.age_max || 35}
          </motion.p>
          <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.7, delay: 1.1 }}
            className="mt-5 text-lg sm:text-xl text-flame font-display uppercase tracking-wide max-w-2xl" data-testid="trip-hook">
            “{trip.hook || "Come alone. Leave with stories and people you'll never forget."}”
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 1.25 }} className="mt-8">
            <button onClick={ctaAction} data-testid="trip-hero-cta"
              className="bg-flame hover:bg-flame/90 text-white rounded-none h-14 px-10 font-bold uppercase tracking-[0.2em] text-sm transition-transform hover:-translate-y-0.5">
              {status.cta}
            </button>
          </motion.div>
        </div>
      </section>

      {/* B. QUICK TRIP INFORMATION */}
      <section className="bg-ink text-cream noise-bg" data-testid="trip-quick-info">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-10 grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-8">
          {quickInfo.map((info, i) => (
            <div key={info.label} data-testid={`trip-info-${info.label.toLowerCase().replace(/\s+/g, "-")}`}>
              <p className="text-[10px] font-bold tracking-[0.2em] uppercase text-cream/50 flex items-center gap-1.5">
                <info.icon className="w-3.5 h-3.5" /> {info.label}
              </p>
              <p className="font-bold mt-2 text-sm">{info.value}</p>
            </div>
          ))}
        </div>
      </section>

      {/* C. WHY YOU'LL LOVE THIS TRIP */}
      <section id="trip-highlights" className="py-16 sm:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp} className="grid grid-cols-1 lg:grid-cols-2 gap-12 mb-14">
            <div>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">Why You'll Love It</p>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">The vibe, mapped out.</h2>
            </div>
            <p className="text-ink/75 leading-relaxed lg:pt-12">{trip.description}</p>
          </motion.div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {(trip.highlights || []).map((h, i) => (
              <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                data-testid={`trip-highlight-${i}`}
                className="bg-white border border-black/10 p-6 hover:shadow-brutal hover:-translate-y-1 transition-all duration-300">
                <span className="font-display text-3xl text-flame">{String(i + 1).padStart(2, "0")}</span>
                <p className="font-bold mt-3 text-sm uppercase tracking-wide">{h}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* PRICING (only when status allows and tiers exist) */}
      {hasPricing && (
        <section className="pb-20 sm:pb-28" data-testid="trip-packages-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="mb-12">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">Packages</p>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Pick your package.</h2>
              {trip.status === "booking_open" && trip.spots_left != null && (
                <p className="mt-4 text-xs font-bold uppercase tracking-widest text-flame" data-testid="trip-spots-remaining">
                  {trip.spots_left} of {trip.spots_total} spots open
                </p>
              )}
            </div>
            {trip.tiers?.basic?.price ? (
              <TierCards tiers={trip.tiers} onSelect={scrollToWaitlist} />
            ) : (
              <p className="font-display text-4xl sm:text-5xl" data-testid="trip-price-label">{trip.price_label}</p>
            )}
          </div>
        </section>
      )}

      {/* D. VISUAL ITINERARY */}
      {trip.itinerary?.length > 0 && (
        <section className="bg-ink text-cream noise-bg py-16 sm:py-24" data-testid="trip-itinerary-section">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <motion.div {...fadeUp} className="mb-14">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">The Itinerary</p>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Day by day.</h2>
            </motion.div>
            <div className="space-y-0 border-l-2 border-cream/15">
              {trip.itinerary.map((day, i) => (
                <motion.div key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.07 }}
                  data-testid={`itinerary-day-${i}`} className="relative pl-8 sm:pl-12 pb-12">
                  <span className="absolute -left-[9px] top-1 w-4 h-4 bg-flame" />
                  <p className="text-xs font-bold tracking-[0.25em] uppercase text-flame">{day.day}</p>
                  <h3 className="font-display text-2xl sm:text-4xl uppercase mt-2 leading-none">{day.title}</h3>
                  <ul className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                    {(day.items || []).map((item, j) => (
                      <li key={j} className="flex items-center gap-3 text-sm text-cream/75">
                        <span className="w-1.5 h-1.5 bg-cream/40 shrink-0" />{item}
                      </li>
                    ))}
                  </ul>
                </motion.div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* E. YOUR STAY */}
      {trip.stay && (trip.stay.name || trip.stay.location) && (
        <section className="py-16 sm:py-24" data-testid="trip-stay-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <motion.div {...fadeUp}>
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">Your Stay</p>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">{trip.stay.name || "Stay TBA"}</h2>
              <div className="mt-6 space-y-3 text-sm">
                {trip.stay.room_config && <p className="font-semibold"><span className="text-ink/50 uppercase tracking-widest text-xs font-bold block">Rooms</span>{trip.stay.room_config}</p>}
                {trip.stay.location && <p className="font-semibold"><span className="text-ink/50 uppercase tracking-widest text-xs font-bold block">Area</span>{trip.stay.location}</p>}
                {trip.stay.amenities?.length > 0 && (
                  <div>
                    <span className="text-ink/50 uppercase tracking-widest text-xs font-bold block mb-2">Amenities</span>
                    <div className="flex flex-wrap gap-2">
                      {trip.stay.amenities.map((a, i) => (
                        <span key={i} className="text-xs font-bold uppercase tracking-widest bg-ink/5 px-3 py-1.5">{a}</span>
                      ))}
                    </div>
                  </div>
                )}
                <p className="text-xs text-ink/50 pt-3">Final accommodation details will be published or provided to confirmed travelers once arrangements are finalized. Exact address and check-in details are shared privately with the confirmed crew.</p>
              </div>
            </motion.div>
            {(trip.stay.images?.length > 0 || trip.gallery?.length > 0) && (
              <motion.div {...fadeUp} className="grid grid-cols-2 gap-4">
                {[...(trip.stay.images || []), ...(trip.gallery || [])].slice(0, 4).map((img, i) => (
                  <img key={i} src={img} alt={`Stay ${i + 1}`} loading="lazy"
                    className={`w-full object-cover border border-black/10 ${i === 0 ? "col-span-2 aspect-[16/9]" : "aspect-square"}`} />
                ))}
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* F + G. INCLUDED / NOT INCLUDED */}
      {(trip.included?.length > 0 || excluded.length > 0) && (
        <section className="pb-16 sm:pb-24" data-testid="trip-included-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 md:grid-cols-2 gap-6">
            <motion.div {...fadeUp} className="bg-white border border-black/10 p-8">
              <h3 className="font-display text-2xl uppercase mb-2 flex items-center gap-3"><Check className="w-6 h-6 text-flame" /> {includedTitle}</h3>
              {trip.status !== "booking_open" ? (
                <p className="text-xs text-ink/50 mb-6" data-testid="planned-inclusions-note">Final inclusions, accommodation, transportation, activities, meals and pricing will be confirmed before booking opens.</p>
              ) : (
                <div className="mb-4" />
              )}
              <ul className="space-y-3">
                {(trip.included || []).map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm" data-testid={`included-${i}`}>
                    <Check className="w-4 h-4 mt-0.5 text-flame shrink-0" /><span className="text-ink/75">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div {...fadeUp} className="bg-ink text-cream noise-bg p-8">
              <h3 className="font-display text-2xl uppercase mb-6 flex items-center gap-3"><X className="w-6 h-6 text-flame" /> What's not</h3>
              <ul className="space-y-3">
                {excluded.map((item, i) => (
                  <li key={i} className="flex items-start gap-3 text-sm" data-testid={`excluded-${i}`}>
                    <X className="w-4 h-4 mt-0.5 text-cream/40 shrink-0" /><span className="text-cream/75">{item}</span>
                  </li>
                ))}
              </ul>
              {trip.cancellation_info && <p className="mt-6 text-xs text-cream/50 border-t border-cream/10 pt-4">{trip.cancellation_info}</p>}
            </motion.div>
          </div>
        </section>
      )}

      {/* TRANSPORT + WHAT TO BRING + IMPORTANT INFO */}
      {(trip.transportation_info || trip.what_to_bring?.length > 0 || trip.important_info || meetingFields.length > 0) && (
        <section className="pb-16 sm:pb-24" data-testid="trip-logistics-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {trip.transportation_info && (
                <motion.div {...fadeUp} className="bg-white border border-black/10 p-8" data-testid="trip-transport-card">
                  <h3 className="font-display text-2xl uppercase mb-4">Getting there & around</h3>
                  <p className="text-sm text-ink/70 leading-relaxed">{trip.transportation_info}</p>
                </motion.div>
              )}
              {trip.what_to_bring?.length > 0 && (
                <motion.div {...fadeUp} className="bg-white border border-black/10 p-8" data-testid="trip-bring-card">
                  <h3 className="font-display text-2xl uppercase mb-4">What to bring</h3>
                  <div className="flex flex-wrap gap-2">
                    {trip.what_to_bring.map((item, i) => (
                      <span key={i} className="text-xs font-bold uppercase tracking-widest bg-ink/5 px-3 py-1.5" data-testid={`bring-item-${i}`}>{item}</span>
                    ))}
                  </div>
                </motion.div>
              )}
              {meetingFields.length > 0 && (
                <motion.div {...fadeUp} className="bg-white border border-black/10 p-8" data-testid="trip-meeting-card">
                  <h3 className="font-display text-2xl uppercase mb-4">Meeting & transfers</h3>
                  <div className="space-y-3">
                    {meetingFields.map(([label, value]) => (
                      <p key={label} className="text-sm">
                        <span className="block text-[10px] font-bold uppercase tracking-widest text-ink/50">{label}</span>
                        <span className="font-semibold">{value}</span>
                      </p>
                    ))}
                  </div>
                </motion.div>
              )}
            </div>
            {trip.important_info && (
              <motion.div {...fadeUp} className="border-l-4 border-flame bg-white border border-black/10 p-6" data-testid="trip-important-info">
                <p className="text-xs font-bold uppercase tracking-widest text-flame mb-2">Important trip information</p>
                <p className="text-sm text-ink/70 leading-relaxed">{trip.important_info}</p>
              </motion.div>
            )}
          </div>
        </section>
      )}

      {/* H. MEET YOUR HOST (only when a real host is assigned) */}
      {trip.host && (
        <section className="pb-16 sm:pb-24" data-testid="trip-host-section">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <motion.div {...fadeUp} className="bg-white border border-black/10 p-8 sm:p-12 flex flex-col sm:flex-row gap-8 items-start shadow-brutal">
              {trip.host.image && (
                <img src={trip.host.image} alt={trip.host.name} className="w-32 h-32 sm:w-40 sm:h-40 object-cover border border-ink/10" />
              )}
              <div>
                <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-2">Meet Your Roamies Host</p>
                <h3 className="font-display text-3xl sm:text-4xl uppercase">{trip.host.name}</h3>
                {trip.host.social && <p className="text-sm font-bold text-flame mt-1">{trip.host.social}</p>}
                <p className="mt-4 text-ink/70 leading-relaxed max-w-xl">{trip.host.bio}</p>
              </div>
            </motion.div>
          </div>
        </section>
      )}

      {/* I. WHO THIS TRIP IS FOR */}
      <section className="bg-cream py-16 sm:py-24 border-t border-ink/10" data-testid="trip-who-for-section">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <motion.div {...fadeUp} className="mb-10">
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">This trip is for you if...</h2>
          </motion.div>
          <div className="space-y-0 border-t border-ink/10">
            {whoFor.map((line, i) => (
              <motion.p key={i} {...fadeUp} transition={{ ...fadeUp.transition, delay: i * 0.05 }}
                data-testid={`who-for-${i}`}
                className="py-5 border-b border-ink/10 text-lg sm:text-2xl font-display uppercase leading-tight flex items-baseline gap-4">
                <span className="text-flame text-sm font-sans font-bold shrink-0">0{i + 1}</span>{line}
              </motion.p>
            ))}
          </div>
        </div>
      </section>

      {/* J. FAQ */}
      {trip.faqs?.length > 0 && (
        <section className="py-16 sm:py-24" data-testid="trip-faq-section">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <motion.div {...fadeUp} className="mb-10">
              <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">FAQ</p>
              <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Asked a lot.</h2>
            </motion.div>
            <div className="border-t border-ink/10">
              {trip.faqs.map((faq, i) => (
                <div key={i} className="border-b border-ink/10" data-testid={`trip-faq-${i}`}>
                  <button onClick={() => setOpenFaq(openFaq === i ? null : i)}
                    data-testid={`trip-faq-toggle-${i}`}
                    className="w-full flex items-center justify-between py-5 text-left font-bold text-sm uppercase tracking-wide hover:text-flame transition-colors">
                    {faq.q}
                    <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${openFaq === i ? "rotate-180 text-flame" : ""}`} />
                  </button>
                  {openFaq === i && <p className="pb-6 text-sm text-ink/70 leading-relaxed">{faq.a}</p>}
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* K. FINAL CTA + WAITLIST */}
      <section ref={waitlistRef} id="waitlist" className="bg-ink text-cream noise-bg py-20 sm:py-28" data-testid="trip-waitlist-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] uppercase text-flame mb-4">{status.label}</p>
            <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]" data-testid="trip-final-cta-heading">
              Ready to meet your next travel crew?
            </h2>
            <p className="mt-6 text-cream/70 max-w-md leading-relaxed">
              {trip.status === "sold_out"
                ? "This one's full — join the waitlist and you'll be first in line for the next drop."
                : trip.status === "completed"
                  ? "This drop already happened. Scroll the highlights, then join the waitlist for the next one."
                  : trip.status === "booking_open"
                    ? (trip.approval_required !== false
                        ? "Booking is open. Submit your application — every application is reviewed before a spot is confirmed. No payment is collected at this stage."
                        : "Booking is open. Join with your details and our team will reach out to confirm your spot.")
                    : "Be first to know when dates, pricing and booking details are released. No payment, no commitment."}
            </p>
            {deadlines.length > 0 && (
              <div className="mt-6 border border-cream/15 p-5 space-y-2" data-testid="trip-deadlines">
                {deadlines.map(([label, value]) => (
                  <p key={label} className="flex justify-between text-sm font-semibold">
                    <span className="text-cream/50 uppercase tracking-widest text-xs">{label}</span>
                    <span>{value}</span>
                  </p>
                ))}
              </div>
            )}
          </div>
          <div className="bg-cream text-ink p-6 sm:p-10">
            {trip.status === "booking_open" && trip.approval_required !== false ? (
              <ApplicationForm trip={trip} />
            ) : (
              <WaitlistForm trips={[trip]} initialTrip={trip.slug} lockTrip heading={status.cta} />
            )}
          </div>
        </div>
      </section>

      {/* Sticky mobile CTA */}
      <div className="md:hidden fixed bottom-0 left-0 right-0 z-40 bg-ink/95 backdrop-blur border-t border-cream/10 p-3" data-testid="trip-sticky-cta">
        <button onClick={ctaAction} data-testid="trip-sticky-cta-btn"
          className="w-full bg-flame text-white h-12 font-bold uppercase tracking-[0.2em] text-xs">
          {status.cta}
        </button>
      </div>
    </div>
  );
}
